import { CheckIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useReviewDmNotification } from '../../../hooks/useDmNotificationMutations';
import { ResolveActivationNotificationModal } from './ResolveActivationNotificationModal';

const REVIEW_LABEL = 'Mark as reviewed';
// DM can't review this type; staff choose whether to also email the patient the activation link.
const PATIENT_APP_NOT_ACTIVATED_EVENT = 'PatientAppNotActivated';

// Stays disabled after success until the refetch drops the row, so it can't be sent twice.
interface ReviewNotificationButtonProps {
    notificationId: string;
    eventType: string;
    onReviewed: (notificationId: string) => Promise<void>;
}

export function ReviewNotificationButton({
    notificationId,
    eventType,
    onReviewed
}: ReviewNotificationButtonProps) {
    const sendsActivationEmail = eventType === PATIENT_APP_NOT_ACTIVATED_EVENT;
    const review = useReviewDmNotification({ onReviewed, sendsActivationEmail });
    const [confirming, setConfirming] = useState(false);
    const busy = review.isPending || review.isSuccess;
    return (
        <>
            <button
                type="button"
                aria-label={REVIEW_LABEL}
                title={REVIEW_LABEL}
                aria-busy={review.isPending}
                disabled={busy}
                onClick={() =>
                    sendsActivationEmail ? setConfirming(true) : review.mutate({ notificationId })
                }
                className="inline-flex h-7 min-w-0 max-w-[11rem] items-center whitespace-nowrap gap-1 rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-white"
            >
                {review.isPending ? (
                    <span
                        aria-hidden="true"
                        className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-teal-600 border-t-transparent"
                    />
                ) : (
                    <CheckIcon aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                )}
                {/* Narrow columns truncate the label; the icon always stays. */}
                <span className="truncate">{REVIEW_LABEL}</span>
            </button>
            {confirming && (
                <ResolveActivationNotificationModal
                    submitting={review.isPending}
                    onConfirm={(sendActivation) => {
                        setConfirming(false);
                        review.mutate({ notificationId, sendActivation });
                    }}
                    onClose={() => setConfirming(false)}
                />
            )}
        </>
    );
}
